import { type ReactElement, useCallback, useMemo, useState } from 'react'
import {
  getName,
  isEmpty,
  isNotEmpty,
  isUserId,
  type UserSearchInput,
  type TeamId,
  type UserId,
  pluralize,
} from '@terros/common'
import { CloseButton, Combobox, Input, type InputBaseProps, Pill, PillsInput, useCombobox } from '@mantine/core'
import { useDebounce } from '@hooks'
import { useProfile, useSearchUsers, useTinyUser } from '@front/model'
import { useDropdownContent } from './options'

type UserMultiInlineSelectProps = {
  teamIds?: TeamId[]
  value: UserId[]
  onChange: (value: UserId[]) => void
  placeholder?: string
  compact?: boolean
  allowEmpty?: boolean
  allowArchived?: boolean
  /** Offer only the signed-in user, for viewers allowed to pick nobody else. */
  selfOnly?: boolean
} & InputBaseProps

export const UserMultiInlineSelect = ({
  teamIds,
  value,
  onChange,
  placeholder = 'Pick users',
  compact = false,
  allowEmpty = false,
  allowArchived = false,
  selfOnly = false,
  ...props
}: UserMultiInlineSelectProps): ReactElement => {
  const { company, user: self } = useProfile()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 300)
  const searchInput: UserSearchInput = useMemo(
    () => ({
      query: debouncedQuery,
      companyId: company?.companyId,
      teamIds,
      options: isEmpty(debouncedQuery) ? { downline: true } : undefined,
      archived: allowArchived && isNotEmpty(debouncedQuery) ? 'all' : undefined,
    }),
    [debouncedQuery, company?.companyId, teamIds, allowArchived]
  )
  const isSearchEnabled = !selfOnly && !props.disabled
  const { data: searchedUsers, isLoading } = useSearchUsers(searchInput, isSearchEnabled)
  const users = isSearchEnabled ? searchedUsers : []

  const combobox = useCombobox({
    onDropdownOpen: () => {
      combobox.focusSearchInput()
      setQuery('')
    },
    onDropdownClose: () => {
      combobox.resetSelectedOption()
    },
  })

  const handleSelect = (userId: string): void => {
    if (userId === 'all') {
      onChange([])
      combobox.closeDropdown()
      return
    }
    if (!isUserId(userId)) return
    if (value.includes(userId)) {
      onChange(value.filter((id) => id !== userId))
    } else {
      onChange([...value, userId])
    }
  }

  const handleRemove = useCallback(
    (userId: UserId): void => {
      onChange(value.filter((id) => id !== userId))
    },
    [onChange, value]
  )

  const handleClearAll = useCallback((): void => {
    onChange([])
  }, [onChange])

  const displayValue = useMemo(() => {
    if (isEmpty(value)) return <Input.Placeholder>{placeholder}</Input.Placeholder>
    if (compact) return `${value.length} ${pluralize(value.length, 'user', 'users')}`
    return value.map((userId) => <UserPill key={userId} userId={userId} onRemove={() => handleRemove(userId)} />)
  }, [value, handleRemove, placeholder, compact])

  const dropdownContent = useDropdownContent({
    users,
    selected: value,
    query,
    allowEmpty,
    isLoading: isLoading && isSearchEnabled,
  })

  const rightSection = useMemo(() => {
    if (isEmpty(value)) return <Combobox.Chevron />

    return (
      <CloseButton
        size='sm'
        onClick={(e) => {
          e.stopPropagation()
          handleClearAll()
        }}
        aria-label='Clear all'
      />
    )
  }, [value, handleClearAll])

  return (
    <Combobox store={combobox} width={350} onOptionSubmit={handleSelect} position='bottom-start'>
      <Combobox.Target>
        <PillsInput
          pointer
          flex={compact ? undefined : 1}
          onClick={() => !props.disabled && combobox.toggleDropdown()}
          rightSection={rightSection}
          rightSectionPointerEvents={isNotEmpty(value) ? 'all' : 'none'}
          {...props}
        >
          <Pill.Group>{displayValue}</Pill.Group>
        </PillsInput>
      </Combobox.Target>
      <Combobox.Dropdown>
        {!selfOnly && (
          <Combobox.Search
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder='Search users'
          />
        )}
        <Combobox.Options mah={400} style={{ overflowY: 'auto' }}>
          {dropdownContent}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

type UserPillProps = {
  userId: UserId
  onRemove: () => void
}

const UserPill = ({ userId, onRemove }: UserPillProps): ReactElement => {
  const { data: user } = useTinyUser(userId)
  const displayName = user ? getName(user) : userId

  return (
    <Pill withRemoveButton onRemove={onRemove}>
      {displayName}
    </Pill>
  )
}
